import { Box } from '@mui/material';
import { Container, Heading, Section, Text } from '../components/atomic';
import SecureEmail from '../components/SecureEmail';
import { LegalPageLayout } from '../layouts';

/**
 * California Data Collection page component for RSM Collective
 *
 * CCPA (California Consumer Privacy Act) compliance page explaining
 * data collection practices and California residents' privacy rights.
 *
 * @returns {JSX.Element} California Data Collection page component
 */
function CaliforniaDataCollection() {
  return (
    <LegalPageLayout>
      <Section
        sx={{
          backgroundColor: 'var(--color-background-light)',
          py: 0,
          position: 'relative',
        }}
      >
        {/* Green Bar Header Treatment */}
        <Box
          sx={{
            borderTop: '4px solid var(--color-primary)',
            borderBottom: '4px solid var(--color-primary)',
            backgroundColor:
              'color-mix(in srgb, var(--color-primary) 5%, transparent)',
            py: 4,
            mb: 6,
          }}
        >
          <Container maxWidth="xl">
            <Box sx={{ textAlign: 'center' }}>
              <Heading
                variant="h1"
                sx={{
                  color: 'text.primary',
                  fontWeight: 600,
                  fontSize: { xs: '2.5rem', md: '3rem' },
                  fontFamily: '"Playfair Display", serif',
                }}
              >
                California Privacy Rights
              </Heading>
            </Box>
          </Container>
        </Box>

        <Container maxWidth="lg" sx={{ pb: 8 }}>
          <Box sx={{ maxWidth: '800px', mx: 'auto' }}>
            {/* Introduction */}
            <Box sx={{ mb: 6 }}>
              <Text sx={{ fontSize: '1.125rem', lineHeight: 1.7, mb: 3 }}>
                This section applies specifically to California residents and
                supplements our Privacy Policy. It describes how RSM Collective
                collects, uses, and shares personal information of California
                consumers and the rights available to them under the California
                Consumer Privacy Act (CCPA).
              </Text>
              <Text
                sx={{
                  fontSize: '1rem',
                  lineHeight: 1.7,
                  mb: 3,
                  fontStyle: 'italic',
                }}
              >
                Effective Date:{' '}
                {new Date().toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </Text>
            </Box>

            {/* Personal Information We Collect */}
            <Box sx={{ mb: 6 }}>
              <Heading
                variant="h2"
                sx={{ mb: 3, fontSize: '1.75rem', fontWeight: 600 }}
              >
                Personal Information We Collect
              </Heading>
              <Text sx={{ mb: 3, lineHeight: 1.7 }}>
                In the past 12 months, we have collected the following
                categories of personal information from California consumers:
              </Text>

              <Box sx={{ mb: 4 }}>
                <Heading
                  variant="h3"
                  sx={{ mb: 2, fontSize: '1.25rem', fontWeight: 600 }}
                >
                  Identifiers
                </Heading>
                <Text sx={{ mb: 2, lineHeight: 1.7 }}>
                  Real name, alias, postal address, unique personal identifier,
                  online identifier, Internet Protocol address, email address,
                  account name, or other similar identifiers.
                </Text>
              </Box>

              <Box sx={{ mb: 4 }}>
                <Heading
                  variant="h3"
                  sx={{ mb: 2, fontSize: '1.25rem', fontWeight: 600 }}
                >
                  Commercial Information
                </Heading>
                <Text sx={{ mb: 2, lineHeight: 1.7 }}>
                  Records of services purchased, obtained, or considered, or
                  other purchasing or consuming histories or tendencies.
                </Text>
              </Box>

              <Box sx={{ mb: 4 }}>
                <Heading
                  variant="h3"
                  sx={{ mb: 2, fontSize: '1.25rem', fontWeight: 600 }}
                >
                  Internet Activity Information
                </Heading>
                <Text sx={{ mb: 2, lineHeight: 1.7 }}>
                  Browsing history, search history, and information regarding
                  your interaction with our website or applications.
                </Text>
              </Box>

              <Box sx={{ mb: 4 }}>
                <Heading
                  variant="h3"
                  sx={{ mb: 2, fontSize: '1.25rem', fontWeight: 600 }}
                >
                  Professional Information
                </Heading>
                <Text sx={{ mb: 2, lineHeight: 1.7 }}>
                  Current or past job history, professional references, and work
                  experience.
                </Text>
              </Box>
            </Box>

            {/* Sources of Personal Information */}
            <Box sx={{ mb: 6 }}>
              <Heading
                variant="h2"
                sx={{ mb: 3, fontSize: '1.75rem', fontWeight: 600 }}
              >
                Sources of Personal Information
              </Heading>
              <Text sx={{ mb: 3, lineHeight: 1.7 }}>
                We obtain personal information from the following categories of
                sources:
              </Text>
              <Box component="ul" sx={{ pl: 4, mb: 4 }}>
                <Text component="li" sx={{ mb: 1, lineHeight: 1.7 }}>
                  Directly from you when you provide it to us
                </Text>
                <Text component="li" sx={{ mb: 1, lineHeight: 1.7 }}>
                  Automatically as you navigate through our site
                </Text>
                <Text component="li" sx={{ mb: 1, lineHeight: 1.7 }}>
                  From third parties, such as business partners
                </Text>
                <Text component="li" sx={{ mb: 1, lineHeight: 1.7 }}>
                  From publicly available sources
                </Text>
              </Box>
            </Box>

            {/* Business Purposes */}
            <Box sx={{ mb: 6 }}>
              <Heading
                variant="h2"
                sx={{ mb: 3, fontSize: '1.75rem', fontWeight: 600 }}
              >
                Business Purposes for Collection
              </Heading>
              <Text sx={{ mb: 3, lineHeight: 1.7 }}>
                We use personal information for the following business purposes:
              </Text>
              <Box component="ul" sx={{ pl: 4, mb: 4 }}>
                <Text component="li" sx={{ mb: 1, lineHeight: 1.7 }}>
                  To provide and maintain our services
                </Text>
                <Text component="li" sx={{ mb: 1, lineHeight: 1.7 }}>
                  To process transactions and fulfill requests
                </Text>
                <Text component="li" sx={{ mb: 1, lineHeight: 1.7 }}>
                  To communicate with you about our services
                </Text>
                <Text component="li" sx={{ mb: 1, lineHeight: 1.7 }}>
                  To improve our website and services
                </Text>
                <Text component="li" sx={{ mb: 1, lineHeight: 1.7 }}>
                  To detect security incidents and protect against fraud
                </Text>
                <Text component="li" sx={{ mb: 1, lineHeight: 1.7 }}>
                  To comply with legal obligations
                </Text>
              </Box>
            </Box>

            {/* Your California Privacy Rights */}
            <Box sx={{ mb: 6 }}>
              <Heading
                variant="h2"
                sx={{ mb: 3, fontSize: '1.75rem', fontWeight: 600 }}
              >
                Your California Privacy Rights
              </Heading>
              <Text sx={{ mb: 3, lineHeight: 1.7 }}>
                As a California resident, you have the following rights under
                the CCPA:
              </Text>

              <Box sx={{ mb: 4 }}>
                <Heading
                  variant="h3"
                  sx={{ mb: 2, fontSize: '1.25rem', fontWeight: 600 }}
                >
                  Right to Know
                </Heading>
                <Text sx={{ mb: 2, lineHeight: 1.7 }}>
                  You have the right to request that we disclose what personal
                  information we collect, use, disclose, and sell about you.
                </Text>
              </Box>

              <Box sx={{ mb: 4 }}>
                <Heading
                  variant="h3"
                  sx={{ mb: 2, fontSize: '1.25rem', fontWeight: 600 }}
                >
                  Right to Delete
                </Heading>
                <Text sx={{ mb: 2, lineHeight: 1.7 }}>
                  You have the right to request that we delete personal
                  information we have collected from you, subject to certain
                  exceptions.
                </Text>
              </Box>

              <Box sx={{ mb: 4 }}>
                <Heading
                  variant="h3"
                  sx={{ mb: 2, fontSize: '1.25rem', fontWeight: 600 }}
                >
                  Right to Opt-Out
                </Heading>
                <Text sx={{ mb: 2, lineHeight: 1.7 }}>
                  You have the right to opt-out of the sale of your personal
                  information. We do not sell personal information.
                </Text>
              </Box>

              <Box sx={{ mb: 4 }}>
                <Heading
                  variant="h3"
                  sx={{ mb: 2, fontSize: '1.25rem', fontWeight: 600 }}
                >
                  Right to Non-Discrimination
                </Heading>
                <Text sx={{ mb: 2, lineHeight: 1.7 }}>
                  We will not discriminate against you for exercising any of
                  your CCPA rights.
                </Text>
              </Box>
            </Box>

            {/* How to Exercise Your Rights */}
            <Box sx={{ mb: 6 }}>
              <Heading
                variant="h2"
                sx={{ mb: 3, fontSize: '1.75rem', fontWeight: 600 }}
              >
                How to Exercise Your Rights
              </Heading>
              <Text sx={{ mb: 3, lineHeight: 1.7 }}>
                To exercise your rights under the CCPA, you may submit a request
                by:
              </Text>
              <Box component="ul" sx={{ pl: 4, mb: 4 }}>
                <Text component="li" sx={{ mb: 1, lineHeight: 1.7 }}>
                  Emailing us at{' '}
                  <SecureEmail
                    emailKey="support"
                    showAsLink={false}
                    component="span"
                  />
                </Text>
                <Text component="li" sx={{ mb: 1, lineHeight: 1.7 }}>
                  Using our online contact form
                </Text>
              </Box>
              <Text sx={{ mb: 3, lineHeight: 1.7 }}>
                We will verify your identity before processing your request.
                This may require you to provide additional information to
                confirm your identity.
              </Text>
            </Box>

            {/* Verification Process */}
            <Box sx={{ mb: 6 }}>
              <Heading
                variant="h2"
                sx={{ mb: 3, fontSize: '1.75rem', fontWeight: 600 }}
              >
                Verification Process
              </Heading>
              <Text sx={{ mb: 3, lineHeight: 1.7 }}>
                To protect your privacy and ensure that personal information is
                not disclosed to unauthorized persons, we need to verify your
                identity before processing your request. The information we
                request for verification may include:
              </Text>
              <Box component="ul" sx={{ pl: 4, mb: 4 }}>
                <Text component="li" sx={{ mb: 1, lineHeight: 1.7 }}>
                  Your name and email address
                </Text>
                <Text component="li" sx={{ mb: 1, lineHeight: 1.7 }}>
                  Account information (if applicable)
                </Text>
              </Box>
            </Box>

            {/* Response Timeline */}
            <Box sx={{ mb: 6 }}>
              <Heading
                variant="h2"
                sx={{ mb: 3, fontSize: '1.75rem', fontWeight: 600 }}
              >
                Response Timeline
              </Heading>
              <Text sx={{ mb: 3, lineHeight: 1.7 }}>
                We will acknowledge receipt of your request within 10 business
                days and respond to your request within 45 days. If we need more
                time (up to 90 days), we will inform you of the reason and
                extension period in writing.
              </Text>
            </Box>

            {/* Authorized Agents */}
            <Box sx={{ mb: 6 }}>
              <Heading
                variant="h2"
                sx={{ mb: 3, fontSize: '1.75rem', fontWeight: 600 }}
              >
                Authorized Agents
              </Heading>
              <Text sx={{ mb: 3, lineHeight: 1.7 }}>
                You may use an authorized agent to submit a request on your
                behalf. If you use an authorized agent, we may require:
              </Text>
              <Box component="ul" sx={{ pl: 4, mb: 4 }}>
                <Text component="li" sx={{ mb: 1, lineHeight: 1.7 }}>
                  Written permission from you for the agent to act on your
                  behalf
                </Text>
                <Text component="li" sx={{ mb: 1, lineHeight: 1.7 }}>
                  Verification of the agent's identity
                </Text>
                <Text component="li" sx={{ mb: 1, lineHeight: 1.7 }}>
                  Direct confirmation from you that you provided the agent
                  permission
                </Text>
              </Box>
            </Box>

            {/* Contact Information */}
            <Box sx={{ mb: 6 }}>
              <Heading
                variant="h2"
                sx={{ mb: 3, fontSize: '1.75rem', fontWeight: 600 }}
              >
                Contact Information
              </Heading>
              <Text sx={{ mb: 3, lineHeight: 1.7 }}>
                If you have any questions about this California Privacy Rights
                notice or need to exercise your rights, please contact us at:
              </Text>
              <Text sx={{ mb: 2, lineHeight: 1.7, fontWeight: 500 }}>
                RSM Collective
                <br />
                Email:{' '}
                <SecureEmail
                  emailKey="support"
                  showAsLink={false}
                  component="span"
                />
                <br />
                Address: 401 West A St. Ste. 200, San Diego, CA 92101
              </Text>
            </Box>
          </Box>
        </Container>
      </Section>
    </LegalPageLayout>
  );
}

export default CaliforniaDataCollection;
